import { useEffect } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate } from "react-router-dom";
import { BrandLogo } from "./theme/BrandLogo";
import { NavIcon } from "./theme/NavIcon";
import { useSession } from "./lib/SessionContext";
import { logout } from "./lib/api";
import {
  analyticsAvailable,
  consentDecision,
  identifyAccount,
} from "./lib/observability";
import { useConnectedCount } from "./lib/useConnectedCount";
import { usePlatformAdmin } from "./lib/usePlatformAdmin";
import { ConsentBanner } from "./components/ConsentBanner";
import { SignIn } from "./pages/SignIn";
import { Pending } from "./pages/Pending";
import { Welcome } from "./pages/Welcome";
import { Dashboard } from "./pages/Dashboard";
import { Companies, CompanyDetailPage } from "./pages/Companies";
import { ApiKeys } from "./pages/ApiKeys";
import { Usage } from "./pages/Usage";
import { Watchlists } from "./pages/Watchlists";
import { ConnectedApps } from "./pages/ConnectedApps";
import { Billing } from "./pages/Billing";
import { Signups } from "./pages/Signups";
import "./ui.css";

function Shell({ children }: { children: React.ReactNode }) {
  const connected = useConnectedCount();
  const isAdmin = usePlatformAdmin();
  const link = ({ isActive }: { isActive: boolean }) =>
    isActive ? "active" : "";
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-row">
          <BrandLogo />
        </div>
        <nav>
          {/* Standalone landing — the at-a-glance view, above the groups. */}
          <NavLink to="/dashboard" className={link}>
            <NavIcon name="dashboard" />
            Overview
          </NavLink>

          {/* Prosumer — the day-to-day screening surfaces. Grows over time. */}
          <div className="nav-group">
            <div className="nav-group-label">Monitoring</div>
            <NavLink to="/companies" className={link}>
              <NavIcon name="companies" />
              Companies
            </NavLink>
            <NavLink to="/watchlists" className={link}>
              <NavIcon name="watchlist" />
              Watchlists
            </NavLink>
          </div>

          {/* Agent connections (MCP). Grows as more AI surfaces land. */}
          <div className="nav-group">
            <div className="nav-group-label">AI</div>
            <NavLink to="/connected-apps" className={link}>
              <NavIcon name="ai" />
              <span>Agents</span>
              {connected != null && connected > 0 && (
                <span className="badge">{connected}</span>
              )}
            </NavLink>
          </div>

          {/* Developer / agent integration. */}
          <div className="nav-group">
            <div className="nav-group-label">Developer</div>
            <NavLink to="/keys" className={link}>
              <NavIcon name="apikeys" />
              API keys
            </NavLink>
          </div>

          {/* Account admin — what the org owner manages. */}
          <div className="nav-group">
            <div className="nav-group-label">Account</div>
            <NavLink to="/usage" className={link}>
              <NavIcon name="usage" />
              Usage
            </NavLink>
            <NavLink to="/billing" className={link}>
              <NavIcon name="billing" />
              Billing
            </NavLink>
          </div>

          {/* Platform-staff only (named admins) — internal queue. */}
          {isAdmin && (
            <div className="nav-group">
              <div className="nav-group-label">Admin</div>
              {/* Platform-staff queue: no dedicated icon. Keep an empty
                  icon slot so its label aligns with the iconed items. */}
              <NavLink to="/signups" className={link}>
                <span className="nav-icon" aria-hidden />
                Signup queue
              </NavLink>
            </div>
          )}
        </nav>

        {/* Utility links live at the foot of the rail, deliberately
         * understated — they are not destinations you reach for daily.
         * Cookie settings stays available because consent must be as
         * easy to withdraw as it was to give (ePrivacy/GDPR); it is not
         * covered by accepting the Terms. */}
        <div className="sidebar-footer">
          {analyticsAvailable() && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new Event("pg-open-consent"));
              }}
            >
              Cookie settings
            </a>
          )}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              logout().then(() => location.reload());
            }}
          >
            Sign out
          </a>
        </div>
      </aside>
      <main>{children}</main>
    </div>
  );
}

export function App() {
  const { loading, authenticated, sub, provisioned, signup_status } =
    useSession();

  // Re-identify on (re)load when the user previously consented. init
  // is done in main.tsx; this only attaches the opaque id.
  useEffect(() => {
    if (authenticated && sub && consentDecision() === "granted") {
      identifyAccount(sub);
    }
  }, [authenticated, sub]);

  if (loading) return <div className="center muted">Loading…</div>;
  if (!authenticated) return <SignIn />;
  // Authenticated but no portal account = private-preview gate page.
  // Approved users get provisioned on the auth/callback round, so
  // ``provisioned=false`` here means pending OR rejected; the message
  // differentiates via signup_status.
  if (!provisioned) return <Pending status={signup_status ?? null} />;

  return (
    <BrowserRouter>
      <Routes>
        {/* Rendered bare, outside the Shell — no sidebar. Reached both
         * fresh (locked account finishing signup) and via the checkout
         * return leg, neither of which is a "dashboard" moment. */}
        <Route path="/welcome" element={<Welcome />} />
        <Route
          path="/*"
          element={
            <Shell>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/companies" element={<Companies />} />
                <Route
                  path="/companies/:companyId"
                  element={<CompanyDetailPage />}
                />
                <Route path="/keys" element={<ApiKeys />} />
                <Route path="/usage" element={<Usage />} />
                <Route path="/watchlists" element={<Watchlists />} />
                <Route path="/connected-apps" element={<ConnectedApps />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/signups" element={<Signups />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Shell>
          }
        />
      </Routes>
      <ConsentBanner accountId={sub} />
    </BrowserRouter>
  );
}
